import React, { memo, useState, useCallback, useEffect, useMemo, Suspense, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import widgetRegistry, { WidgetType } from '../widgets/widgetRegistry';
import { widgetImports } from '../widgets/widgetImports';
import { createDefaultWidgetData } from '../utils/widgetDefaults';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIMARY_COLOR = '#EE4446';

// ============================================================================
// DYNAMIC WIDGET VARIANTS GENERATION
// ============================================================================

/**
 * Dynamically generate widget variants from widgetImports
 * This ensures a single source of truth - adding a widget to widgetImports
 * automatically makes it available in the style selection modal
 */
const generateWidgetVariants = (): Record<string, string[]> => {
  const variants: Record<string, string[]> = {};
  const prefixes = ['Basic', 'Classic', 'Essential', 'Prime', 'Modern'];
  
  // Get all widget keys from widgetImports (single source of truth)
  const widgetKeys = Object.keys(widgetImports);
  
  widgetKeys.forEach(widgetKey => {
    // Skip layout widgets (they don't follow the variant pattern)
    if (widgetKey.includes('Layout') || widgetKey.includes('Promo')) {
      return;
    }
    
    // Find matching prefix
    for (const prefix of prefixes) {
      if (widgetKey.includes(prefix)) {
        // Extract base type (remove prefix)
        let baseType = widgetKey.replace(prefix, '');
        
        // Normalize base types to match expected names
        // "PopularItems" -> "Popular"
        // "ExperienceCard" -> "Experience"
        // "ContactForm" -> "Contact"
        // "Events" -> "Event"
        // "SocialFollow" -> "Social"
        baseType = baseType
          .replace('Items', '')
          .replace('Card', '')
          .replace('Form', '')
          .replace('Events', 'Event')
          .replace('Follow', '');
        
        // Initialize array if doesn't exist
        if (!variants[baseType]) {
          variants[baseType] = [];
        }
        
        // Add this variant
        variants[baseType].push(widgetKey);
        break;
      }
    }
  });
  
  return variants;
};

// Generate widget variants dynamically from widgetImports
const WIDGET_VARIANTS = generateWidgetVariants();

// ============================================================================
// TYPES
// ============================================================================

export interface WidgetStyleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  widgetType: string; // The base widget type (e.g., "Banner", "Nav")
  currentVariant: string; // The current variant (e.g., "BasicBanner")
  currentData?: any; // The actual widget data to preview
  onSelectVariant: (newVariant: string) => void;
  isLoading?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Extract base type from widget type (e.g., "BasicBanner_ABC123" -> "Banner")
const getBaseType = (widgetType: string): string => {
  // Remove unique ID if present
  let typeWithoutId = widgetType.includes('_') ? widgetType.split('_')[0] : widgetType;
  
  // Remove variant prefix (Basic, Classic, Essential, Prime)
  const variants = ['Basic', 'Classic', 'Essential', 'Prime'];
  for (const variant of variants) {
    if (typeWithoutId.includes(variant)) {
      typeWithoutId = typeWithoutId.replace(variant, '');
      break;
    }
  }
  
  // Normalize base type (same as generateWidgetVariants)
  return typeWithoutId
    .replace('Items', '')
    .replace('Card', '')
    .replace('Form', '')
    .replace('Events', 'Event')
    .replace('Follow', '');
};

// Extract variant name (e.g., "BasicBanner" -> "Basic")
const getVariantName = (fullType: string): string => {
  const variants = ['Basic', 'Classic', 'Essential', 'Prime'];
  for (const variant of variants) {
    if (fullType.startsWith(variant)) {
      return variant;
    }
  }
  return 'Basic'; // default
};

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

const WidgetStyleSelectionModal = memo(function WidgetStyleSelectionModal({
  isOpen,
  onClose,
  widgetType,
  currentVariant,
  currentData,
  onSelectVariant,
  isLoading = false,
}: WidgetStyleSelectionModalProps) {
  // ============================================================================
  // STATE & REFS
  // ============================================================================
  
  const [selectedVariant, setSelectedVariant] = useState<string>(currentVariant);
  const [currentIndex, setCurrentIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================
  
  // Get base type from widget type
  const baseType = useMemo(() => getBaseType(widgetType), [widgetType]);
  
  // Get available variants for this widget type
  const availableVariants = useMemo(() => 
    WIDGET_VARIANTS[baseType] || [], 
    [baseType]
  );

  // Extract current variant type without ID
  const currentVariantType = useMemo(() => 
    currentVariant.includes('_') ? currentVariant.split('_')[0] : currentVariant,
    [currentVariant]
  );

  // ============================================================================
  // CALLBACKS
  // ============================================================================
  
  const handleSave = () => {
    if (selectedVariant) {
      onSelectVariant(selectedVariant);
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') swiperRef.current?.slidePrev();
      if (e.key === 'ArrowRight') swiperRef.current?.slideNext();
    },
    [onClose]
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Preload widget imports when modal opens
  useEffect(() => {
    if (isOpen && availableVariants.length > 0) {
      availableVariants.forEach(variant => {
        const importFn = widgetImports[variant as keyof typeof widgetImports];
        importFn?.();
      });
    }
  }, [isOpen, availableVariants]);
  
  // Auto-select current variant when modal opens
  useEffect(() => {
    if (isOpen && currentVariantType) {
      setSelectedVariant(currentVariantType);
      const index = availableVariants.indexOf(currentVariantType);
      if (index !== -1) {
        setCurrentIndex(index);
        // Swiper will handle the initial slide via initialSlide prop
      }
    }
  }, [isOpen, currentVariantType, availableVariants]);

  // Keyboard navigation
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // ============================================================================
  // RENDER PREVIEW
  // ============================================================================
  
  const renderPreview = useCallback((variant: string) => {
    // Get component from registry
    const WidgetComponent = widgetRegistry[variant as WidgetType];
    
    if (!WidgetComponent) {
      return (
        <div className="flex w-full items-center justify-center rounded border border-gray-200 bg-gray-50 p-8">
          <p className="text-gray-500 text-xs">Preview not available</p>
        </div>
      );
    }

    // ✅ Use default data for preview instead of actual data
    // This ensures preview shows consistent mock content for all variants
    // Actual user data remains completely untouched
    const defaultData = createDefaultWidgetData(variant);

    return (
      <div className="relative w-full h-[420px] overflow-hidden rounded border border-gray-200 bg-white">
        <div className="scale-[0.65] origin-top pointer-events-none w-full">
          <Suspense fallback={
            <div className="p-10 text-center">
              <div className="text-gray-400 text-sm">Loading preview...</div>
            </div>
          }>
            <WidgetComponent
              data={defaultData}
              isEditing={false}
              onUpdate={() => {}}
            />
          </Suspense>
        </div>
      </div>
    );
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (!isOpen) return null;

  // If no variants available, show error
  if (availableVariants.length === 0) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Variants Available</h3>
            <p className="text-gray-600 mb-4">
              No style variants found for widget type: {baseType}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get initial slide index
  const initialSlideIndex = availableVariants.indexOf(currentVariantType);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="widget-style-modal-title"
    >
      <div className="flex w-full max-w-7xl max-h-[90vh] flex-col rounded-2xl bg-white shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div>
            <h2 id="widget-style-modal-title" className="text-xl font-semibold text-gray-900">
              Change {baseType} Layout
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Preview and choose a layout for your {baseType.toLowerCase()} widget. Use the arrow keys or tabs to explore different styles.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-gray-100"
            aria-label="Close modal"
          >
            <FiX className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="pt-3 pb-4 h-[500px] overflow-hidden">
          {/* Tab Navigation with Animated Underline */}
          <div className="px-6 relative mb-3 border-b border-gray-200">
            <div className="flex justify-start gap-1">
              {availableVariants.map((variant, index) => {
                const variantName = getVariantName(variant);
                const isActive = currentIndex === index;
                
                return (
                  <button
                    key={variant}
                    onClick={() => swiperRef.current?.slideTo(index)}
                    role="tab"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    className={`
                      relative px-6 py-3 font-medium transition-colors duration-200
                      ${isActive ? 'text-red-600' : 'text-gray-600 hover:text-gray-900'}
                    `}
                  >
                    {variantName}

                    {/* Animated sliding underline */}
                    {isActive && (
                      <motion.div
                        layoutId="tab-underline"
                        className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full"
                        style={{ backgroundColor: PRIMARY_COLOR }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Swiper Carousel */}
          <Swiper
            spaceBetween={30}
            slidesPerView={1}
            initialSlide={initialSlideIndex >= 0 ? initialSlideIndex : 0}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              setCurrentIndex(index);
              setSelectedVariant(availableVariants[index]);
            }}
            speed={600}
            loop={false}
            className="w-full h-full px-6"
          >
            {availableVariants.map((variant) => (
              <SwiperSlide key={variant}>
                <div
                  className={`rounded-lg border-2 overflow-hidden transition-all ${
                    selectedVariant === variant
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200'
                  }`}
                >
                  {renderPreview(variant)}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedVariant || isLoading}
            className={`rounded-lg px-6 py-2 font-medium transition ${
              selectedVariant && !isLoading
                ? 'text-white hover:opacity-90'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
            style={selectedVariant && !isLoading ? { backgroundColor: PRIMARY_COLOR } : undefined}
          >
            {isLoading ? 'Applying...' : `Apply ${getVariantName(selectedVariant || '')} Style`}
          </button>
        </div>
        
      </div>
    </div>
  );
});

WidgetStyleSelectionModal.displayName = 'WidgetStyleSelectionModal';
export default WidgetStyleSelectionModal;
